import React, { useState, useEffect } from "react";
import userIcon from "../../icons/userIcon.svg";
import PhoneInput from "../ui/PhoneInput";
import HouseKeeping from "./HouseKeeping";
import AddOns from "./AddOns";
import { baseUrl } from "../../Environments/environment.js";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer, Bounce } from "react-toastify";
import { ThreeDot } from "react-loading-indicators";
import { ArrowLeft } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const RoomService = () => {
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("United States +1");
  const [loading, setLoading] = useState(false);
  const [showAddOns, setShowAddOns] = useState(false);
  const [guestSessionId, setGuestSessionId] = useState(null);
  const [loadingMoreServices, setLoadingMoreServices] = useState(false);
  const [isRegistered, setIsRegistered] = useState(null);
  const [isPmsConnected, setIsPmsConnected] = useState(null);
  const [checkingProperty, setCheckingProperty] = useState(true);
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [searchParams] = useSearchParams();
  const roomno = searchParams.get("roomno");

  const { propertyId } = useParams();
  const cCode = countryCode.split(" ")[countryCode.split(" ").length - 1];

  const onCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
  };

  const onPhoneNumberChange = (e) => {
    console.log(e.target.value);
    setPhoneNumber(e.target.value);
  };

  // Check property details to see if PMS is connected
  const checkPropertyDetails = async () => {
    if (!propertyId) return;
    
    try {
      setCheckingProperty(true);
      const response = await axios.get(
        `${baseUrl}/houseKeepingv2/${propertyId}/propertyDetails`,
      );
      
      // Check if PMS is connected (adjust field name based on actual API response)
      let pmsConnected = false;
      if(response.data.result?.pmsEnabled && response.data.result?.pmsId) {
        pmsConnected = true;
      }
      setIsPmsConnected(pmsConnected);
      
      // If PMS is not connected, go directly to housekeeping page
      if (!pmsConnected) {
        setStep(2);
      }
    } catch (error) {
      console.error("Error fetching property details:", error);
      // If API fails, assume PMS is not connected and go to housekeeping
      setIsPmsConnected(false);
      setStep(2);
    } finally {
      setCheckingProperty(false);
    }
  };

  // Check if room number is present
  useEffect(() => {
    if (!roomno) {
      setInvalidUrl(true);
      setCheckingProperty(false);
      toast.error("Invalid URL: Room number is required");
    } else {
      setInvalidUrl(false);
    }
  }, [roomno]);

  useEffect(() => {
    if (roomno && propertyId) {
      checkPropertyDetails();
    }
  }, [propertyId, roomno]);

  const handleNext = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      
      console.log(phoneNumber);
      console.log(cCode);

      // If PMS is connected, mobile number is required
      if (isPmsConnected) {
        if (!phoneNumber || phoneNumber.trim() === "") {
          toast.error("Mobile number is required");
          setLoading(false);
          return;
        }
        // Make API call to verify phone number and get guest session
        const response = await axios.get(
          `${baseUrl}/houseKeepingv2/${propertyId}/getCurrentGuest`,
          {
            params: { phoneNumber, countryCode: cCode }
          }
        );
         
        if(response.status === 200 && response.data.result.guestSession) {
          const sessionId = response.data.result.guestSession._id;
          if (sessionId) {
            setGuestSessionId(sessionId);
            setStep(2);
          } else {
            toast.error("Please enter the registered number");
          }
        } else if(response.status === 200 && response.data.result.propertyData) {
          // If only propertyData is returned without guestSession, number is invalid
          toast.error("Please enter the registered number");
        } else {
          toast.error("Please enter the registered number");
        }
      } else {
        // If PMS is not connected, proceed without guestSessionId
        setStep(2);
      }
    } catch (error) {
      // If PMS is connected and we get an error, the number is invalid
      if (isPmsConnected) {
        toast.error("Please enter the registered number");
      } else if (error.response && error.response.data) {
        Object.keys(error.response.data.error || {}).map((key) => {
          toast.error(`${error.response.data.error[key][0]}`);
        });
      } else {
        toast.error("An error occurred. Please try again.");
      }
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (showAddOns) {
      // If AddOns is shown, go back to HouseKeeping (step 2)
      setShowAddOns(false);
    } else if (step === 2) {
      // If on HouseKeeping, go back to step 1
      setStep(1);
    }
  };

  // Handle More Services click - make API call and show AddOns
  const handleMoreServicesClick = async () => {
    try {
      setLoadingMoreServices(true);
      console.log(propertyId);
      
      // Make API call to get guest session
      const response = await axios.get(
        `${baseUrl}/guest/${propertyId}/getRegisteredGuest`,
        {
          params: { phoneNumber, countryCode: cCode }
        }
      );

      setLoadingMoreServices(false);
      // Check if registered based on response status
      if (response.status === 200 && response.data.result.guestSession) {
        setIsRegistered(true);
        if (response.data.result.guestSession._id) {
          // Store guestSessionId from response
          const sessionId = response.data.result.guestSession._id;
          setGuestSessionId(sessionId);
          // Show AddOns component
          setShowAddOns(true);
        } else {
          toast.error("Unable to load services. Please try again.");
        }
      } else {
        setIsRegistered(false);
        // Navigate to registration or show error
        toast.error("Please enter the registerd phone number.");
        // Go back to step 1 (phone number entry)
        setStep(1);
      }
    } catch (error) {
      setLoadingMoreServices(false);
      setIsRegistered(false);
      
      // Check if error has response and status
      if (error.response) {
        // If status is not 200, user is not registered
        if (error.response.status !== 200) {
          setIsRegistered(false);
          toast.error("Please register to access more services.");
          // Go back to step 1 (phone number entry)
          setStep(1);
        } else {
          // Error with 200 status (unlikely but handle it)
          if (error.response.data) {
            Object.keys(error.response.data.error || {}).map((key) => {
              toast.error(`${error.response.data.error[key][0]}`);
            });
          }
        }
      } else {
        toast.error("An error occurred. Please try again.");
      }
      console.error("Error:", error);
    }
  };

  // Common Header Component
  const RoomServiceHeader = ({ showBackButton = false, onBack }) => {
    if (!showBackButton) return null;
    
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center px-5 py-4">
          <button
            onClick={onBack}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Go back"
          >
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <div className="flex-1 text-center">
            <h2 className="text-lg font-semibold text-white font-poppins">
              Our Services
            </h2>
          </div>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>
      </div>
    );
  };

  // Step controller - similar to Registration.jsx
  const renderStep = () => {
    // Show error if room number is missing
    if (invalidUrl) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 px-5">
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white font-poppins mb-2">
              Invalid URL
            </h2>
            <p className="text-gray-300 font-poppins">
              Room number is required. Please use a valid URL with room number parameter.
            </p>
          </div>
        </div>
      );
    }

    // If AddOns should be shown, render it
    if (showAddOns && guestSessionId) {
      return (
        <>
          <RoomServiceHeader showBackButton={true} onBack={handleBack} />
          <div className="pt-16">
            <AddOns guestSessionId={guestSessionId} onBack={handleBack} />
          </div>
        </>
      );
    }

    switch (step) {
      case 1:
        return (
          <form onSubmit={(e) => handleNext(e)}>
            <div className=" mx-5 mt-5 text-neutral-800">
              <div className="flex justify-between items-center font-poppins h-full">
                <div className="bg-neutral-200 px-3 p-1 rounded-lg text-sm font-poppins">
                  Our Services
                </div>
                <div>
                  <img
                    src={userIcon}
                    alt="userIcon"
                    className="object-contain w-6 h-6 "
                  />
                </div>
              </div>
              <div className="rounded-xl p-5 bg-neutral-100 my-5">
                <div className="flex flex-col gap-2">
                  <PhoneInput
                    countryCodes={["+1", "+91"]}
                    countryCode={countryCode}
                    phoneNumber={phoneNumber}
                    onPhoneNumberChange={onPhoneNumberChange}
                    onCountryCodeChange={onCountryCodeChange}
                  />
                </div>
                <div className="flex justify-between items-center w-full mt-4 ">
                  <button
                    className="w-full px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    type="submit"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </form>
        );
      case 2:
        return (
          <>
            <RoomServiceHeader showBackButton={true} onBack={handleBack} />
            <div className="pt-16">
              <HouseKeeping 
                onMoreServicesClick={handleMoreServicesClick} 
                isRegistered={isRegistered}
                roomno={roomno}
                guestSessionId={guestSessionId}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderStep()}
      {/* loading */}
      {(loading || loadingMoreServices || checkingProperty) && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-20 flex justify-center items-center z-50">
          <ThreeDot
            variant="brick-stack"
            color="#4f46e5"
            size="medium"
            text=""
            textColor=""
          />
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </>
  );
};

export default RoomService;
